import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { setLocalStorage } from '../aaa';
import { SpinnerOverlay } from '../SpinnerOverlay';
import StudentHeroSegment from '../components/StudentHeroSegment';
import ToDoCard from '../components/ToDoCard';
import FilterToggle from '../components/FilterToggle';
import SortDropdown from '../components/SortDropdown';
import { RootState } from '../ApplicationState';
import { selectClassroom } from '../Classrooms';
import { getMiddleSchoolModule, listStudentAssignments } from '../BackendInterface';
import styles from './ToDosPage.module.scss';
import toDoHeader from '../images/ToDo_Page.png';
import toDoHeaderHS from '../images/ToDo_Page_HS.png';
import { Assignment } from '@peekapak/types/LessonPlanTypes';
import { isCheckInModule } from '../GlobalFunctions';

interface Props {
  setCurrentToDo: React.Dispatch<React.SetStateAction<any>>;
  setGroupedToDoInfo: React.Dispatch<React.SetStateAction<any>>;
}

const dropdownArray = [
  { text: 'Date Assigned', value: 'createdAt' },
  { text: 'Last Edited', value: 'modifiedAt' },
];

const ToDosPage = ({ setCurrentToDo, setGroupedToDoInfo }: Props) => {
  const [activeFilter, setActiveFilter] = useState(['inProgress', 'submitted', 'assigned', 'retracted']);
  const [sortMethod, setSortMethod] = useState('createdAt');
  const [toDoArray, setToDoArray] = useState<Assignment[]>([]);
  const [displayArray, setDisplayArray] = useState<Assignment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [toDoHeaderImage, setToDoHeaderImage] = useState<string>('');

  const userId = useSelector((state: RootState) => {
    return state.user.userProfile!.userId;
  });
  const grade = useSelector((state: RootState) => {
    return state.user.userProfile!.grade;
  });

  const classroom = useSelector((state: RootState) => {
    const selectedClassroom = selectClassroom(state);
    return selectedClassroom;
  });

  const userProfile = useSelector((state: RootState) => {
    return state.user.userProfile;
  });

  const language = classroom?.language || 'en';

  const history = useHistory();

  const handleDropdownChange = (e: React.FormEvent<HTMLSelectElement>) => {
    setSortMethod(e.currentTarget.value);
  };

  const handleClick = (value: string[]) => {
    setActiveFilter(value);
  };

  const handleCardClick = (cardObject: Assignment) => {
    setCurrentToDo(cardObject);
    setLocalStorage('currentToDo', cardObject);
    const groupToDosByModule = displayArray.filter(
      (item: Assignment) =>
        item.module === cardObject.module && item.unit === cardObject.unit && item.lesson === cardObject.lesson,
    );
    const quizFirst = groupToDosByModule.sort((a: Assignment, b: Assignment) => a.todo.localeCompare(b.todo));
    if (quizFirst.length > 1) setGroupedToDoInfo(quizFirst);
    setLocalStorage('groupedToDoInfo', quizFirst);
    if (isCheckInModule(cardObject.todo)) {
      history.push({
        pathname: '/studentportal/checkin',
        state: cardObject,
      });
    } else {
      history.push(
        `/studentportal/activity/${cardObject.module}/${cardObject.language ?? language}/${grade}/toDo/${cardObject.createdAt}`,
      );
    }
  };

  const sortAndFilter = useCallback(async () => {
    try {
      const displayCopy = [...toDoArray];

      const filteredCopy = displayCopy.filter((obj) => {
        return activeFilter.includes(obj.status);
      });
      const sortedCopy = filteredCopy.sort((a, b) => {
        return b[sortMethod] - a[sortMethod];
      });
      return setDisplayArray(sortedCopy);
    } finally {
      setIsLoading(false);
    }
  }, [sortMethod, activeFilter, toDoArray]);

  const getToDos = useCallback(async () => {
    try {
      setIsLoading(true);
      const allAssignments = await listStudentAssignments(userId, language, grade);

      const moduleIds = [...new Set(allAssignments.map((a: Assignment) => a.module))];
      const moduleResults = await Promise.allSettled(
        moduleIds.map((moduleId: string) => getMiddleSchoolModule(moduleId, language, String(grade))),
      );

      const moduleValidTodos = new Map<string, Set<string>>();
      moduleResults.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          const moduleData = (result.value as any)?.data;
          const studentContent: any[] = moduleData?.studentContent || [];
          const validIds = new Set(studentContent.map((item: any) => item.id));
          if (validIds.size > 0) moduleValidTodos.set(moduleIds[index], validIds);
        }
      });

      const filtered = allAssignments.filter((a: Assignment) => {
        const validIds = moduleValidTodos.get(a.module);
        return validIds ? validIds.has(a.todo) : true;
      });

      setToDoArray(filtered);
    } catch {
      console.log('Error fetching student ToDos');
    } finally {
      setIsLoaded(true);
    }
  }, [userId, language, grade]);

  useEffect(() => {
    if (!isLoaded) getToDos();
    sortAndFilter();
  }, [sortAndFilter, activeFilter, getToDos, isLoaded]);

  const getToDoHeaderImage = useCallback(async () => {
    try {
      if (userProfile && userProfile.type === 'Teacher' && classroom) {
        if (classroom.grade > 8) {
          setToDoHeaderImage(toDoHeaderHS);
        } else {
          setToDoHeaderImage(toDoHeader);
        }
      } else if (userProfile && userProfile.type === 'Student') {
        if (userProfile.grade > 8) {
          setToDoHeaderImage(toDoHeaderHS);
        } else {
          setToDoHeaderImage(toDoHeader);
        }
      }
    } catch (error) {
      console.error(error);
    }
  }, [userProfile, classroom]);

  useEffect(() => {
    if (!toDoHeaderImage) getToDoHeaderImage();
  }, [classroom, userProfile, toDoHeaderImage, getToDoHeaderImage]);

  return (
    <>
      <SpinnerOverlay isShow={isLoading || !toDoHeaderImage} />
      <StudentHeroSegment home backgroundImage={toDoHeaderImage}>
        <div className={styles.heroText}>
          <h1>To Dos</h1>
          <h3>In-class and at-home assignments</h3>
        </div>
      </StudentHeroSegment>
      <div className={styles.cardContainer}>
        <div className={styles.filterRow}>
          <div className={styles.filterToggles}>
            <FilterToggle
              handleClick={handleClick}
              filterValues={['inProgress', 'assigned', 'retracted']}
              activeFilter={activeFilter}
            >
              Open
            </FilterToggle>
            <FilterToggle
              handleClick={handleClick}
              filterValues={['inProgress', 'submitted', 'assigned', 'retracted']}
              activeFilter={activeFilter}
            >
              All
            </FilterToggle>
            <FilterToggle
              handleClick={handleClick}
              filterValues={['inProgress', 'retracted']}
              activeFilter={activeFilter}
            >
              In Progress
            </FilterToggle>
            <FilterToggle handleClick={handleClick} filterValues={['submitted']} activeFilter={activeFilter}>
              Submitted
            </FilterToggle>
          </div>
          <SortDropdown options={dropdownArray} value={sortMethod} onChange={handleDropdownChange} />
        </div>
        {displayArray?.length > 0 &&
          displayArray?.map((x: Assignment) => (
            <ToDoCard key={x.createdAt} toDoObject={x} handleClick={handleCardClick} grade={parseInt(grade)} />
          ))}
      </div>
    </>
  );
};

export default ToDosPage;
